import React, { useEffect, useState, useCallback } from 'react';
import { Container, Card, CardBody, Row, Col, Button, Spinner } from 'react-bootstrap';
import { CheckCircleFill, XCircleFill, ArrowRepeat } from 'react-bootstrap-icons'; // Added XCircleFill for error, ArrowRepeat for retry
import axios from 'axios'; // Import axios for API calls
import { backendbaseUrl as backendurl } from '../../../Utils/baseUrl';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

// --- Configuration for API (Adjust as per your project's setup) ---
// In a real application, backendbaseUrl and axiosInstance would likely be imported from a separate utility file.
// For this single-file demonstration, they are defined here.
const backendbaseUrl = backendurl; // IMPORTANT: Replace with your actual backend URL

const axiosInstance = axios.create({
    baseURL: backendbaseUrl,
    headers: {
        'Content-Type': 'application/json',
        // Add any other headers like Authorization tokens if required by your API
        // 'Authorization': `Bearer YOUR_AUTH_TOKEN`,
    },
});


// --- CSS Styles Embedded ---
const styles = `
/* Ensure Inter font is used if available, otherwise fallback to sans-serif */


/* Animated Background Gradient */
.background-gradient-animated {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(45deg, #84fab0, #8fd3f4, #a6c1ee, #fbc2eb);
    background-size: 400% 400%;
    animation: gradientShift 15s ease infinite;
    z-index: -1; /* Send to back */
    opacity: 0.7; /* Make it subtle */
}

@keyframes gradientShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
}

.yuri-card {
  border-radius: 12px; /* Slightly more rounded */
  overflow: hidden;
  transition: all 0.3s ease-in-out;
  margin-bottom: 1.5rem;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1); /* Stronger, softer shadow */
  background: rgba(255, 255, 255, 0.95); /* Slightly transparent white */
  backdrop-filter: blur(5px); /* Frosted glass effect */
  border: none; /* Remove default border */
}

.yuri-card:hover {
  transform: translateY(-5px); /* More pronounced lift on hover */
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15) !important;
}

.success-icon-wrapper, .error-icon-wrapper, .verifying-icon-wrapper {
  font-size: 6rem; /* Larger icon */
  margin-bottom: 2rem;
}

.success-icon-wrapper {
  color: #28a745; /* Deeper success green */
  animation: bounceIn 0.8s ease-out forwards, pulseGlow 2s infinite alternate; /* Add pulse glow */
}

.error-icon-wrapper {
  color: #dc3545; /* Bootstrap danger red */
  animation: shake 0.5s ease-in-out; /* Shake animation for error */
}

.verifying-icon-wrapper {
    color: #0d6efd; /* Bootstrap primary blue */
    animation: spin 1.5s linear infinite; /* Spin animation for verifying */
}

/* New pulse glow animation for icon */
@keyframes pulseGlow {
    0% { transform: scale(1); filter: drop-shadow(0 0 0px rgba(40, 167, 69, 0)); }
    100% { transform: scale(1.05); filter: drop-shadow(0 0 15px rgba(40, 167, 69, 0.6)); }
}

/* Spin animation for verifying state */
@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

/* Shake animation for error state */
@keyframes shake {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-10px); }
    20%, 40%, 60%, 80% { transform: translateX(10px); }
}

/* General Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideInRight {
  from { transform: translateX(30px); opacity: 0; } /* More pronounced slide */
  to { transform: translateX(0); opacity: 1; }
}

@keyframes fadeInUp {
  from { transform: translateY(30px); opacity: 0; } /* More pronounced slide */
  to { transform: translateY(0); opacity: 1; }
}

@keyframes bounceIn {
  0% {
    transform: scale(0.3);
    opacity: 0;
  }
  50% {
    transform: scale(1.2); /* Bouncier */
    opacity: 1;
  }
  70% {
    transform: scale(0.95);
  }
  100% {
    transform: scale(1);
  }
}

.animated-fade-in {
  animation: fadeIn 0.8s ease-out forwards;
}

.animated-slide-in-right {
  animation: slideInRight 0.8s ease-out forwards;
  animation-delay: 0.3s; /* Adjusted delay */
}

.animated-fade-in-up {
  animation: fadeInUp 0.8s ease-out forwards;
  animation-delay: 0.6s; /* Adjusted delay */
}

.animated-button {
    animation: fadeInUp 0.8s ease-out forwards;
    animation-delay: 0.9s; /* Adjusted delay */
}

/* Confetti effect */
.confetti-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none; /* Allow clicks to pass through */
    overflow: hidden;
}

.confetti-piece {
    position: absolute;
    width: 10px;
    height: 10px;
    background-color: var(--color);
    opacity: 0;
    animation: confettiFall var(--duration) ease-out var(--delay) forwards;
    border-radius: 50%; /* Make them circular */
}

@keyframes confettiFall {
    0% {
        transform: translate(var(--startX), var(--startY)) rotate(0deg);
        opacity: 0;
    }
    10% {
        opacity: 1;
    }
    100% {
        transform: translate(var(--endX), var(--endY)) rotate(720deg);
        opacity: 0;
    }
}

/* Tailwind CSS-like utility classes for responsiveness and styling */
.min-vh-100 {
    min-height: 100vh;
}

.d-flex {
    display: flex;
}

.align-items-center {
    align-items: center;
}

.justify-content-center {
    justify-content: center;
}

.w-100 {
    width: 100%;
}

.py-5 {
    padding-top: 3rem !important;
    padding-bottom: 3rem !important;
}

.p-4 {
    padding: 1.5rem !important;
}

.text-center {
    text-align: center;
}

.text-success {
    color: #28a745 !important; /* Deeper success green */
}

.text-danger {
    color: #dc3545 !important; /* Bootstrap danger red */
}

.text-primary {
    color: #0d6efd !important; /* Bootstrap primary blue */
}

.lead {
    font-size: 1.35rem; /* Slightly larger lead text */
    font-weight: 400; /* Slightly bolder */
}

.text-muted {
    color: #6c757d !important;
}

.mb-0 {
    margin-bottom: 0 !important;
}

.mb-1 {
    margin-bottom: 0.25rem !important;
}

.mb-3 {
    margin-bottom: 1rem !important;
}

.mb-4 {
    margin-bottom: 1.5rem !important;
}

.mt-4 {
    margin-top: 1.5rem !important;
}

.d-grid {
    display: grid;
}

.gap-2 {
    gap: 0.5rem;
}

.d-md-block {
    display: block !important;
}

.me-md-3 {
    margin-right: 1rem !important;
}

/* Button styling for consistency with Yuri theme */
.btn {
    border-radius: 0.75rem; /* Even more rounded buttons */
    padding: 0.85rem 1.8rem; /* Larger padding */
    font-weight: 600;
    transition: all 0.3s ease-in-out;
    letter-spacing: 0.5px; /* Slightly more spaced letters */
    text-transform: uppercase; /* Uppercase for buttons */
}

.btn-primary {
    background-color: #0d6efd;
    border-color: #0d6efd;
    color: white;
    box-shadow: 0 4px 10px rgba(13, 110, 253, 0.3); /* Button shadow */
}

.btn-primary:hover {
    background-color: #0b5ed7;
    border-color: #0a58ca;
    transform: translateY(-2px); /* Lift on hover */
    box-shadow: 0 6px 15px rgba(13, 110, 253, 0.4);
}

.btn-outline-secondary {
    border-color: #6c757d;
    color: #6c757d;
}

.btn-outline-secondary:hover {
    background-color: #6c757d;
    color: white;
    transform: translateY(-2px); /* Lift on hover */
}

.btn-danger {
    background-color: #dc3545;
    border-color: #dc3545;
    color: white;
}

.btn-danger:hover {
    background-color: #c82333;
    border-color: #bd2130;
}

/* Responsive adjustments for columns */
@media (min-width: 768px) {
    .col-md-8 {
        flex: 0 0 auto;
        width: 66.66666667%;
    }
    .offset-md-2 {
        margin-left: 16.66666667%;
    }
}

@media (min-width: 992px) {
    .col-lg-6 {
        flex: 0 0 auto;
        width: 50%;
    }
    .offset-lg-3 {
        margin-left: 25%;
    }
}
`;

// Inject styles into the head of the document
if (typeof window !== 'undefined') {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
}

// --- Interfaces for Component Props ---
interface PaymentSuccessPageProps {
    // These props are now optional as the component will simulate data if not provided
    transactionId?: string;
    amount?: number;
    currency?: string;
    planName?: string;
    onViewDetailsClick?: (transactionId: string) => void;
    onGoHomeClick?: () => void;
    // New prop to pass the track_id for API verification
    trackId?: string;
    // New prop to simulate API response (for testing success/failure without a real backend)
    simulateApiSuccess?: boolean; // true for API success, false for API failure
    apiVerificationDelay?: number; // Delay in ms for API verification (default 2000)
}

// --- PaymentSuccessPage Component ---
const PaymentSuccessPage: React.FC<PaymentSuccessPageProps> = ({
    transactionId: propTransactionId,
    amount: propAmount,
    currency: propCurrency,
    planName: propPlanName,
    onViewDetailsClick,
    onGoHomeClick,
    trackId: propTrackId, // New prop for trackId
    simulateApiSuccess = true, // Default to API success for demonstration
    apiVerificationDelay = 2000, // Default 2 seconds for API call
}) => {
    // State to manage the current phase of payment processing
    const [paymentStatus, setPaymentStatus] = useState<'verifying' | 'success' | 'failed'>('verifying');
    // State to trigger animations for the success/failure screen
    const [animateContent, setAnimateContent] = useState(false);

    // Internal state for demonstration purposes if props are not provided
    const [transactionInfo, setTransactionInfo] = useState({
        id: propTransactionId || 'TXN-DEMO-' + Math.random().toString(36).substring(2, 11).toUpperCase(),
        amount: propAmount || parseFloat((Math.random() * 500 + 50).toFixed(2)),
        currency: propCurrency || '$',
        plan: propPlanName || (Math.random() > 0.5 ? 'Pro Tier' : 'Enterprise Solution'),
        track_id: propTrackId || 'track-' + Math.random().toString(36).substring(2, 12), // Dummy track_id
    });

    const { id } = useParams();

    // Function to handle payment verification via API
    const verifyPayment = useCallback(async () => {
        setPaymentStatus('verifying');
        setAnimateContent(false); // Reset animation state for re-verification


        try {
            // Simulate network delay for API call
            await new Promise(resolve => setTimeout(resolve, apiVerificationDelay));


            // In a real application, you would replace this with your actual API call
            // For demonstration, we'll use a simulated axios call
            const response = await axiosInstance.post(
                `${backendbaseUrl}/company/plan/proration-calculation/payment/confirmation`,
                { track_id: id }
            );

            if (response.status === 200) {
                setPaymentStatus('success');
                setTimeout(() => setAnimateContent(true), 50); // Trigger success animations
            } else {
                // Handle non-200 status codes as failure
                console.error("Payment verification failed with status:", response.status, response.data);
                setPaymentStatus('failed');
                setTimeout(() => setAnimateContent(true), 50); // Trigger failure animations
            }
        } catch (error: any) {
            // Handle API call errors (e.g., network issues, server errors)
            console.error("Error during payment verification:", error);
            setPaymentStatus('failed');
            setTimeout(() => setAnimateContent(true), 50); // Trigger failure animations
        }
    }, [transactionInfo.track_id, apiVerificationDelay]); // Depend on track_id and delay

    useEffect(() => {
        verifyPayment(); // Start verification when component mounts
    }, [verifyPayment, id]);

    // Function to generate confetti pieces (only for success state)
    const generateConfetti = () => {
        const confettiPieces = [];
        const colors = ['#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#2196f3', '#03a9f4', '#00bcd4', '#009688', '#4CAF50', '#8bc34a', '#cddc39', '#ffeb3b', '#ffc107', '#ff9800', '#ff5722', '#795548', '#9e9e9e', '#607d8b'];

        for (let i = 0; i < 50; i++) { // Generate 50 confetti pieces
            const randomColor = colors[Math.floor(Math.random() * colors.length)];
            const startX = `${Math.random() * 100}vw`; // Random start X across viewport
            const startY = `${-Math.random() * 50}px`; // Start above viewport
            const endX = `${Math.random() * 100}vw`; // Random end X across viewport
            const endY = `${100 + Math.random() * 50}vh`; // End far below viewport
            const duration = `${3 + Math.random() * 3}s`; // 3 to 6 seconds
            const delay = `${Math.random() * 2}s`; // 0 to 2 seconds delay

            confettiPieces.push(
                <div
                    key={i}
                    className="confetti-piece"
                    style={{
                        '--color': randomColor,
                        '--startX': startX,
                        '--startY': startY,
                        '--endX': endX,
                        '--endY': endY,
                        '--duration': duration,
                        '--delay': delay,
                    } as React.CSSProperties} // Type assertion for custom CSS variables
                ></div>
            );
        }
        return confettiPieces;
    };

    // Custom message box function (replaces alert())
    const showMessageBox = (title: string, message: string, onConfirm?: () => void, buttonText: string = "Close") => {
        const messageBox = document.createElement('div');
        messageBox.innerHTML = `
            <div style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); display: flex; justify-content: center; align-items: center; z-index: 1000;">
                <div style="background: white; padding: 20px; border-radius: 8px; text-align: center; box-shadow: 0 4px 15px rgba(0,0,0,0.2);">
                    <h4>${title}</h4>
                    <p>${message}</p>
                    <button id="closeMessageBox" style="background-color: #0d6efd; color: white; padding: 10px 20px; border: none; border-radius: 5px; cursor: pointer; margin-top: 15px;">${buttonText}</button>
                </div>
            </div>
        `;
        document.body.appendChild(messageBox);
        document.getElementById('closeMessageBox')?.addEventListener('click', () => {
            document.body.removeChild(messageBox);
            if (onConfirm) onConfirm();
        });
    };

    const navigate = useNavigate();
    // Default handlers if props are not provided (for standalone demo)
    const handleViewDetails = onViewDetailsClick || (() => {
        showMessageBox("Viewing Transaction Details", `Transaction ID: <strong>${transactionInfo.id}</strong><br/>Details would be displayed here or you'd navigate to a dedicated page.`);
    });

    const handleGoHome = onGoHomeClick || (() => {
        navigate('/company/user-management')


    });

    return (
        <div className="min-vh-100 d-flex flex-column">
            {/* Animated Background */}
            <div className="background-gradient-animated"></div>

            {/* Confetti Container (only for success state) */}
            {paymentStatus === 'success' && animateContent && <div className="confetti-container">{generateConfetti()}</div>}

            <Container className="d-flex align-items-center justify-content-center flex-grow-1 py-5" style={{ position: 'relative', zIndex: 1 }}>
                <Row className="w-100">
                    <Col md={{ span: 8, offset: 2 }} lg={{ span: 6, offset: 3 }}>
                        <Card className={`yuri-card text-center p-4 ${animateContent ? 'animated-fade-in' : ''}`}>
                            <CardBody>
                                {/* Verifying State */}
                                {paymentStatus === 'verifying' && (
                                    <div className="verifying-state animated-fade-in">
                                        <div className="verifying-icon-wrapper">
                                            <Spinner animation="border" role="status" className="text-primary" style={{ width: '6rem', height: '6rem' }} />
                                        </div>
                                        <h2 className="mb-3 text-primary">Verifying Payment...</h2>
                                        <p className="lead text-muted mb-4">
                                            Please wait while we confirm your transaction. This may take a few moments.
                                        </p>
                                    </div>
                                )}

                                {/* Success State */}
                                {paymentStatus === 'success' && (
                                    <div className="success-state">
                                        <div className="success-icon-wrapper">
                                            <CheckCircleFill />
                                        </div>
                                        <h2 className={`mb-3 text-success ${animateContent ? 'animated-slide-in-right' : ''}`}>
                                            Payment Successful!
                                        </h2>
                                        <p className={`lead text-muted mb-4 ${animateContent ? 'animated-fade-in-up' : ''}`}>
                                            Your payment was successful.
                                        </p>

                                        {/* <div className={`mb-4 text-start ${animateContent ? 'animated-fade-in-up' : ''}`} style={{ animationDelay: '0.7s' }}>
                                            <p className="mb-1"><strong>Transaction ID:</strong> {transactionInfo.id}</p>
                                            <p className="mb-1"><strong>Amount Paid:</strong> {transactionInfo.currency}{transactionInfo.amount.toFixed(2)}</p>
                                            {transactionInfo.plan && <p className="mb-0"><strong>Plan:</strong> {transactionInfo.plan}</p>}
                                        </div> */}

                                        <div className={`d-grid gap-2 d-md-block mt-4 ${animateContent ? 'animated-button' : ''}`}>
                                            {/* <Button
                                                variant="primary"
                                                size="lg"
                                                onClick={() => handleViewDetails(transactionInfo.id)}
                                                className="me-md-3 mb-3 mb-md-0"
                                            >
                                                View Details
                                            </Button> */}
                                            <Button
                                                variant="outline-secondary"
                                                size="lg"
                                                onClick={handleGoHome}
                                            >
                                                Go to Dashboard
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {/* Failed State */}
                                {paymentStatus === 'failed' && (
                                    <div className="failed-state animated-fade-in">
                                        <div className="error-icon-wrapper">
                                            <XCircleFill />
                                        </div>
                                        <h2 className="mb-3 text-danger">Payment Failed!</h2>
                                        <p className="lead text-muted mb-4">
                                            Unfortunately, your payment could not be processed. Please try again or contact support.
                                        </p>
                                        <div className="d-grid gap-2 d-md-block mt-4">
                                            <Button
                                                variant="danger"
                                                size="lg"
                                                onClick={verifyPayment} // Retry payment
                                                className="me-md-3 mb-3 mb-md-0"
                                            >
                                                <ArrowRepeat className="me-2" /> Try Again
                                            </Button>
                                            <Button
                                                variant="outline-secondary"
                                                size="lg"
                                                onClick={handleGoHome}
                                            >
                                                Go to Dashboard
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default PaymentSuccessPage;
